import React, { FC } from 'react';
import { useFormikContext, getIn } from 'formik';

import { FormInput, Row, Column, HRule } from 'Shared/components';
import { PartnerFormValues } from 'Admin/Partners/components';

import styles from './styles.module.css';

type AgencyPdfSettingsType = {
  ancestorIds?: string[];
};

export const AgencyPdfSettings: FC<AgencyPdfSettingsType> = ({
  ancestorIds,
}) => {
  const {
    values,
    touched,
    errors,
    setFieldValue,
    handleChange,
    handleBlur,
  } = useFormikContext<PartnerFormValues>();

  const isSubAgency = ancestorIds?.length ?? 0 > 1;

  // need to reuse the same mini component in multiple layouts/scenarios
  const customInfoToggle = (
    <FormInput
      name="pdfSettingsCustomInfo"
      variant="radioButtonGroup"
      data-testid="pdf-settings-custom-info"
      label="What would you like to display on the Quote PDF?"
      onChange={value => {
        setFieldValue(
          'quotePdfSettings.customizeContactInformation',
          value === 'custom'
        );
      }}
      options={{
        aor: 'Display Agent of Record',
        custom: 'Display custom information',
      }}
      value={
        values.quotePdfSettings?.customizeContactInformation ? 'custom' : 'aor'
      }
    />
  );

  // need to reuse the same mini component in multiple layouts/scenarios
  const customInfoForm = (
    <>
      <Row>
        <Column variant="half">
          <FormInput
            variant="text"
            name="quotePdfSettings.contactInformation.agencyName"
            label="Agency Display Name"
            value={values.quotePdfSettings?.contactInformation?.agencyName}
            onChange={handleChange}
            onBlur={handleBlur}
            errorMessage={
              getIn(touched, 'quotePdfSettings.contactInformation.agencyName')
                ? getIn(
                    errors,
                    'quotePdfSettings.contactInformation.agencyName'
                  )
                : undefined
            }
          />
        </Column>
        <Column variant="half">
          <FormInput
            variant="text"
            name="quotePdfSettings.contactInformation.agentName"
            label="Agent Display Name"
            value={values.quotePdfSettings?.contactInformation?.agentName}
            onChange={handleChange}
            onBlur={handleBlur}
            errorMessage={
              getIn(touched, 'quotePdfSettings.contactInformation.agentName')
                ? getIn(errors, 'quotePdfSettings.contactInformation.agentName')
                : undefined
            }
          />
        </Column>
      </Row>
      <Row>
        <Column variant="half">
          <FormInput
            type="email"
            variant="text"
            name="quotePdfSettings.contactInformation.emailAddress"
            label="Display Email Address"
            value={values.quotePdfSettings?.contactInformation?.emailAddress}
            onChange={handleChange}
            onBlur={handleBlur}
            errorMessage={
              getIn(touched, 'quotePdfSettings.contactInformation.emailAddress')
                ? getIn(
                    errors,
                    'quotePdfSettings.contactInformation.emailAddress'
                  )
                : undefined
            }
          />
        </Column>
        <Column variant="half">
          <FormInput
            variant="text"
            name="quotePdfSettings.contactInformation.phoneNumber"
            label="Display Phone Number"
            value={values.quotePdfSettings?.contactInformation?.phoneNumber}
            onChange={handleChange}
            onBlur={handleBlur}
            errorMessage={
              getIn(touched, 'quotePdfSettings.contactInformation.phoneNumber')
                ? getIn(
                    errors,
                    'quotePdfSettings.contactInformation.phoneNumber'
                  )
                : undefined
            }
          />
        </Column>
      </Row>
    </>
  );

  return (
    <>
      <HRule size="lg" />

      <h3>Quote PDF contact information</h3>

      <p>
        This will allow you to adjust the agent/agency contact information that
        displays on the customer's quote PDF.
      </p>

      {isSubAgency ? (
        <>
          <Row>
            <Column variant="half">
              <FormInput
                name="pdfSettingsInheritInfo"
                variant="radioButtonGroup"
                data-testid="pdf-settings-inherit-info"
                label="Inherit parent agency's quote PDF settings"
                onChange={value => {
                  setFieldValue(
                    'quotePdfSettings.inheritParentInformation',
                    value === 'yes'
                  );
                }}
                options={{
                  yes: 'Yes',
                  no: 'No',
                }}
                value={
                  values.quotePdfSettings?.inheritParentInformation
                    ? 'yes'
                    : 'no'
                }
              />
            </Column>
            {!values.quotePdfSettings?.inheritParentInformation && (
              <Column variant="half">{customInfoToggle}</Column>
            )}
          </Row>

          {/*
            Inherit from the Parent Agency
            
            - Use Parent's custom contact information
            - Or Parent is using Agent of Record
          */}
          {values.quotePdfSettings?.inheritParentInformation &&
            values.quotePdfSettings?.readOnlySettings &&
            (values.quotePdfSettings?.readOnlySettings?.quote_pdf
              .customize_contact_information ? (
              <>
                <Row>
                  <Column variant="quarter">
                    <div className={styles.parentInfoLabel}>
                      Agency Display Name
                    </div>
                    <div className={styles.parentInfoText}>
                      {values.quotePdfSettings?.readOnlySettings.quote_pdf
                        .custom_contact_information?.agency_name || '—'}
                    </div>
                  </Column>
                  <Column variant="quarter">
                    <div className={styles.parentInfoLabel}>
                      Agent Display Name
                    </div>
                    <div className={styles.parentInfoText}>
                      {values.quotePdfSettings?.readOnlySettings.quote_pdf
                        .custom_contact_information?.agent_name || '—'}
                    </div>
                  </Column>
                </Row>
                <Row>
                  <Column variant="quarter">
                    <div className={styles.parentInfoLabel}>
                      Display Email Address
                    </div>
                    <div className={styles.parentInfoText}>
                      {values.quotePdfSettings?.readOnlySettings.quote_pdf
                        .custom_contact_information?.email_address || '—'}
                    </div>
                  </Column>
                  <Column variant="quarter">
                    <div className={styles.parentInfoLabel}>
                      Display Phone Number
                    </div>
                    <div className={styles.parentInfoText}>
                      {values.quotePdfSettings?.readOnlySettings.quote_pdf
                        .custom_contact_information?.phone_number || '—'}
                    </div>
                  </Column>
                </Row>
              </>
            ) : (
              <div className={styles.parentInfoText}>
                Parent agency is currently using Agent of Record on customer
                quote PDFs
              </div>
            ))}

          {!values.quotePdfSettings?.inheritParentInformation &&
            values.quotePdfSettings?.customizeContactInformation &&
            customInfoForm}
        </>
      ) : (
        <>
          {/* Carrier or Principal Agency */}
          <Row>
            <Column variant="half">{customInfoToggle}</Column>
          </Row>

          {values.quotePdfSettings?.customizeContactInformation &&
            customInfoForm}
        </>
      )}
    </>
  );
};
